import { ApiNeighborContext } from '../api-types/neighbor.api';
import { ApiSponsor, ApiSponsorExtended, ApiSponsorPerson, CreateSponsorPersonApiBody } from '../api-types/sponsor.api';
import { NeighborContext } from '../app-types/neighbors.app';
import { CreateSponsorPerson, Sponsor, SponsorExtended, SponsorPerson } from '../app-types/sponsor.app';
import { transformApiAddressToAppAddress, transformAppCreateAddressToApiCreateAddressBody } from './address.transform';
import { transformApiContactPointsToAppContactPoints } from './contact-point.transform';
import { transformApiGenderToAppGender } from './gender.transform';

/**
 * Transforms an API Sponsor object to an App Sponsor object.
 * @param input The API Sponsor object to transform.
 * @returns The transformed App Sponsor object.
 */
export function transformApiSponsorToAppSponsor(input: ApiSponsor): Sponsor {
	return {
		id: input.id,
		name: input.name,
		integrationOfficeId: input.integrationOfficeId,
	};
}

/**
 * Transforms an array of API Sponsor objects to an array of App Sponsor objects.
 * @param inputs The array of API Sponsor objects to transform.
 * @returns The array of transformed App Sponsor objects.
 */
export function transformApiSponsorsToAppSponsors(inputs: ApiSponsor[]): Sponsor[] {
	return inputs.map(transformApiSponsorToAppSponsor);
}

export function transformApiSponsorToAppSponsorExtended(input: ApiSponsorExtended): SponsorExtended {
	return {
		id: input.id,
		name: input.name,
		integrationOfficeId: input.integrationOfficeId,
		category: input.category,
		address: input.address ? transformApiAddressToAppAddress(input.address) : undefined,
		contactPoints: transformApiContactPointsToAppContactPoints(input.contactPoints ?? []),
	};
}

export function transformApiSponsorPersonToAppSponsorPerson(person: ApiSponsorPerson): SponsorPerson {
	return {
		id: person.id,
		name: person.name,
		firstName: person.firstName,
		title: person.title,
		role: person.role,
		remark: person.remark,
		hasBeenClarified: person.hasBeenClarified,
		clarifiedAt: person.clarifiedAt ?? undefined,
		typeOfClarification: person.typeOfClarification ?? undefined,
		address: person.address ? transformApiAddressToAppAddress(person.address) : undefined,
		contactPoints: transformApiContactPointsToAppContactPoints(person.contactPoint ?? person.contactPoints ?? []),
		gender: person.gender ? transformApiGenderToAppGender(person.gender) : undefined,
		personType: person.personType ?? person.categoryPersonType,
		isDeactivated: person.isDeactivated,
	};
}

export function transformApiSponsorPersonsToAppSponsorPersons(input: ApiSponsorPerson[]): SponsorPerson[] {
	return input.map(transformApiSponsorPersonToAppSponsorPerson);
}
export function transformCreateSponsorPersonToApiBody(input: CreateSponsorPerson): CreateSponsorPersonApiBody {
	return {
		firstName: input.firstName,
		title: input.title ?? null,
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: input.clarifiedAt ?? null,
		typeOfClarification: input.typeOfClarification ?? null,
		remark: input.remark ?? null,
		genderId: input.genderId ?? null,
		address: transformAppCreateAddressToApiCreateAddressBody(input.address),
		contactPoints: (input.communicationWays || []).map((cp) => ({
			remark: cp.remark ?? null,
			categoryContactPointId: cp.categoryContactPointId ?? null,
			contactValue: cp.contactValue ?? null,
			preferredWayOfContact: cp.preferredWayOfContact,
		})),
		sponsorId: input.sponsorId,
		role: input.role ?? null,
		name: input.lastName,
	};
}

export function transformApiSponsorPersonNeighborsToAppSponsorPersonNeighbors(
	input: ApiNeighborContext<ApiSponsorPerson>,
): NeighborContext<SponsorPerson> {
	return {
		current: transformApiSponsorPersonToAppSponsorPerson(input.current),
		previous: input.previous ? transformApiSponsorPersonToAppSponsorPerson(input.previous) : null,
		next: input.next ? transformApiSponsorPersonToAppSponsorPerson(input.next) : null,
	};
}
